import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import EChartCustomized, {
    SCHEMES,
    TOOLTIP_OPTIONS,
} from '@/components/EchartCustomized'

interface SLAComplianceChartProps {
    meetingSLA: number
    breachingSLA: number
}

/**
 * Donut chart showing SLA compliance distribution
 * (services meeting SLA vs breaching SLA)
 */
const SLAComplianceChart: React.FC<SLAComplianceChartProps> = ({
    meetingSLA,
    breachingSLA,
}) => {
    const { t } = useTranslation('services')
    const total = meetingSLA + breachingSLA

    const options = useMemo(() => {
        if (total === 0) return null

        return {
            tooltip: {
                trigger: 'item',
                ...TOOLTIP_OPTIONS,
                formatter: (params: any) => {
                    return `<b>${params.name}</b><br/>
                        Số lượng: ${params.value}<br/>
                        Tỷ lệ: ${params.percent}%`
                },
            },
            legend: {
                orient: 'horizontal',
                bottom: 0,
                left: 'center',
                textStyle: { fontSize: 11 },
            },
            series: [
                {
                    name: 'SLA Compliance',
                    type: 'pie',
                    radius: ['45%', '70%'],
                    center: ['50%', '45%'],
                    avoidLabelOverlap: false,
                    itemStyle: {
                        borderRadius: 6,
                        borderColor: '#fff',
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: 'center',
                        formatter: () => {
                            const percent =
                                total > 0
                                    ? Math.round((meetingSLA / total) * 100)
                                    : 0
                            return `{value|${percent}%}\n{label|Tuân thủ}`
                        },
                        rich: {
                            value: {
                                fontSize: 24,
                                fontWeight: 'bold',
                                color:
                                    meetingSLA >= breachingSLA
                                        ? SCHEMES.success[0]
                                        : SCHEMES.error[0],
                            },
                            label: {
                                fontSize: 12,
                                color: SCHEMES.mono.gray[1],
                                padding: [4, 0, 0, 0],
                            },
                        },
                    },
                    emphasis: {
                        label: { show: true },
                    },
                    labelLine: { show: false },
                    data: [
                        {
                            value: meetingSLA,
                            name: t('sla.meeting_sla', 'Đạt SLA'),
                            itemStyle: { color: SCHEMES.success[0] },
                        },
                        {
                            value: breachingSLA,
                            name: t('sla.breaching_sla', 'Vi phạm SLA'),
                            itemStyle: { color: SCHEMES.error[0] },
                        },
                    ],
                },
            ],
        }
    }, [meetingSLA, breachingSLA, total, t])

    if (!options) {
        return (
            <div className="flex h-full items-center justify-center text-sm text-gray-400">
                Không có dữ liệu
            </div>
        )
    }

    return (
        <div style={{ height: '220px' }}>
            {/* @ts-ignore */}
            <EChartCustomized options={options} />
        </div>
    )
}

export default SLAComplianceChart
